import React from 'react'
import { CountriesProductDto } from '@/api/Clients/Types/CountriesProduct'
import { normalizeProductsForSubmission } from '@/api/Clients/Types/TickmillProductDto'
import { UpdateCountryProduct } from '@/api/Clients/Types/UpdateCountryProduct'
import { FormCheckboxField, FormReadonlyField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FormikErrors, FormikProps, withFormik } from 'formik'
import { useAppSelector } from 'store'

import { CountryProductsField } from './CountryProductsField'

const EditCountryProductFormUI: React.FC<FormikProps<UpdateCountryProduct> & OuterProps> = (
  props
) => {
  const { handleSubmit, errors, isSubmitting, data, values, onClose } = props
  const tickmillProducts = useAppSelector((state) => state.clients.tickmillProducts)

  const handleSubmitForm = () => {
    props.setSubmitting(true)
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`Edit ${data.tickmillCompany.name} for ${data.country.name}`}</DialogTitle>
        </DialogHeader>
        <FormReadonlyField label='Tickmill Company' value={data.tickmillCompany.name} />
        <FormReadonlyField label='Country' value={data.country.name} />
        <FormCheckboxField name='isHidden' label='Hidden' value={values.isHidden} />
        <CountryProductsField
          className='md:col-span-2'
          label='Tickmill Products'
          availableProducts={tickmillProducts}
          products={values.products}
          error={errors.products as string | string[] | undefined}
          onChange={(products) => props.setFieldValue('products', products)}
        />
        <FormReadonlyField label='Link' value={data.forcedLink} canCopy />
        <DialogFooter>
          <Button disabled={isSubmitting} onClick={onClose} variant='outline'>
            Cancel
          </Button>
          <Button disabled={isSubmitting} onClick={handleSubmitForm}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface OuterProps {
  isOpen?: boolean
  onClose(): void
  data: CountriesProductDto

  onSubmit(companyCountryId: string, values: UpdateCountryProduct): Promise<void>
}

const dataToSubmit = (values: UpdateCountryProduct): UpdateCountryProduct => {
  return {
    isHidden: values.isHidden,
    products: normalizeProductsForSubmission(values.products),
  }
}

export const EditCountryProductForm = withFormik<OuterProps, UpdateCountryProduct>({
  mapPropsToValues: ({ data: countryProduct }) => {
    return {
      isHidden: countryProduct?.isHidden ?? false,
      products: countryProduct.products,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(props.data.id, dataToSubmit(values))
    } catch (e) {
      console.error(e)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<UpdateCountryProduct> = {}
    if (!values.products.length) {
      errors.products = 'Required'
    }
    return errors
  },
  enableReinitialize: true,
})(EditCountryProductFormUI)
